import { ColorPicker } from "@@/shared/colors/color-picker";
import { FlexColumn } from "@@/shared/flex-containers";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { useFormId } from "@@/shared/form/form-id.context";
import type {
    DeepKeysOf,
    DeepPathValueType,
    HexColor,
    RequiredTranslatable,
} from "@towni/common";
import type { ZodSchema } from "zod";
import { useForm3Field } from "./form3.use-form-field";

type ValueType = HexColor;

type Props<FieldId> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;
    readonly title: RequiredTranslatable;
    readonly defaultColor?: HexColor;
};

const createForm3ColorPickerComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3ColorPicker = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
        FieldValue extends ValueType = ValueType,
    >(
        props: Props<FieldId>,
    ) => {
        // Assert that the fieldId has a value of the correct type
        const formIdFromContext = useFormId({ doNotThrow: true });
        const formId = props.formId || formIdFromContext;
        const field = useForm3Field<FormSchema, FormData, FieldId, FieldValue>({
            fieldId: props.fieldId,
        });

        if (!field)
            throw new Error(
                `Field ${props.fieldId} in form ${formId} not found`,
            );

        return (
            <FlexColumn
                className={props.className}
                key={`${formId}_${props.fieldId}`}
                crossAxis="stretch">
                <ColorPicker
                    key={`${formId}_${props.fieldId}`}
                    color={field.value}
                    title={props.title}
                    onSelect={hexColor => {
                        field.setValue(
                            hexColor as DeepPathValueType<FormData, FieldId>,
                        );
                    }}
                />
            </FlexColumn>
        );
    };
    return Form3ColorPicker;
};

export { createForm3ColorPickerComponent };
